import { useState } from 'react';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Label } from '@givebutter/primitives/label';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { Separator } from '@givebutter/primitives/separator';
import { maskWholeNumber, pluralize } from '@givebutter/util';
import { getEngageSenders, getMessageNumbers, updateEngageMessageById } from 'api/engage';
import * as Row from 'assets/js/pages/engage/Row';
import EmailPreview from 'assets/js/pages/engage/components/MessageBuilder/EmailPreview';
import { StepNavigation } from 'assets/js/pages/engage/components/MessageBuilder/StepNavigation';
import * as Form from 'components/form';
import * as MessageBuilder from 'components/message-builder';
import * as Tooltip from 'components/tooltip';
import { MessageChannel } from 'constants/messages';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentEngageMessage } from 'customHooks/useCurrentEngageMessage';
import { dateToServerDateTime, isoDateFromServer } from 'helpers/dates';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { upperFirst } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { EngageMessage } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const StepThree = () => {
    const { accountId, title } = useCurrentAccount();
    const { data: message } = useCurrentEngageMessage();
    const [isDateFieldEnabled, setIsDateFieldEnabled] = useState(message.sending_at !== null);

    const { data: senders } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageSenders(accountId),
        queryFn: () => getEngageSenders(accountId),
        select: response => response.data.data,
    });

    const { data: numbers } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageNumbers(accountId),
        queryFn: () => getMessageNumbers(accountId),
    });

    const possibleSender = (message.channel === 'email' ? senders : numbers).find(
        item => item.id === message.sender?.id,
    );
    const sender = possibleSender ? getFormattedPhoneNumber(possibleSender.value) : undefined;
    const channel = message.channel === 'sms' ? 'SMS' : upperFirst(message.channel);

    const methods = Form.useGivebutterForm<{ sending_at?: DateTime | null }>({
        defaultValues: {
            sending_at: message.sending_at ? [isoDateFromServer(message.sending_at)] : undefined,
        },
    });

    const { control, setValue, setError, clearErrors } = methods;

    const { mutate: saveMessage, isPending } = useMutation<
        EngageMessage,
        GivebutterAxiosError,
        { sending_at: DateTime | null }
    >({
        mutationFn: ({ sending_at }) =>
            updateEngageMessageById(accountId, message.id, {
                sending_at: sending_at ? dateToServerDateTime(sending_at) : null,
            }),
        onSuccess: response => {
            queryClient.setQueryData(
                accountQueryKeys.engageMessageById(accountId, message.id),
                response,
            );
            clearErrors();
        },
        onError: e => {
            const message = e.response?.data.message;
            if (message) setError('sending_at', { message });
        },
    });

    return (
        <Form.GivebutterFormProvider {...methods}>
            <div className={'flex flex-col gap-8'}>
                <Box className={'flex flex-col gap-4 p-4'}>
                    <div className="flex flex-col gap-4">
                        <p className="text-xl font-medium">Schedule</p>
                        <RadioGroup.Root
                            className={'w-fit'}
                            defaultValue={'send-later'}
                            value={isDateFieldEnabled ? 'send-later' : 'send-now'}
                            onValueChange={value => {
                                const isSendLater = value === 'send-later';
                                setIsDateFieldEnabled(isSendLater);
                                if (!isSendLater) {
                                    setValue('sending_at', null);
                                    saveMessage({ sending_at: null });
                                }
                            }}
                        >
                            <RadioGroup.ItemContainer>
                                <RadioGroup.Item id={'send-now'} value={'send-now'}>
                                    <RadioGroup.Indicator />
                                </RadioGroup.Item>
                                <Label htmlFor={'send-now'} className={'leading-none'}>
                                    Send now
                                </Label>
                            </RadioGroup.ItemContainer>
                            <RadioGroup.ItemContainer>
                                <RadioGroup.Item id={'send-later'} value={'send-later'}>
                                    <RadioGroup.Indicator />
                                </RadioGroup.Item>
                                <Label htmlFor={'send-later'} className={'leading-none'}>
                                    Schedule for later
                                </Label>
                            </RadioGroup.ItemContainer>
                        </RadioGroup.Root>
                    </div>
                    {isDateFieldEnabled && (
                        <div className={'flex flex-col gap-1'}>
                            <Label htmlFor={'send-date'}>
                                Date &amp; Time ({DateTime.now().zoneName}
                                {' - '}
                                {DateTime.now().offsetNameShort})
                            </Label>
                            <div className={'[&>*]:w-fit'}>
                                <Form.UseFormDateTimePicker
                                    control={control}
                                    name="sending_at"
                                    allowRange={false}
                                    minDate={DateTime.now()}
                                    startDateTimeProps={{
                                        clearable: true,
                                        startInputProps: { placeholder: 'Select date' },
                                    }}
                                    onChange={value => {
                                        saveMessage({ sending_at: value[0] ? value[0] : null });
                                    }}
                                />
                            </div>
                            <Form.Error name="sending_at" />
                        </div>
                    )}
                </Box>
                <Box className={'flex flex-col gap-4 p-4'}>
                    <p className="text-xl font-medium">{channel} Preview</p>
                    <div className={'flex flex-col gap-2'}>
                        <Row.Root>
                            <Row.Title className={'col-span-2 md:col-span-1'}>To:</Row.Title>
                            <Row.Value className={'col-span-10 md:col-span-11'}>
                                <Tooltip.Root>
                                    <Tooltip.Trigger asChild>
                                        <p className={'flex items-center gap-1'}>
                                            {maskWholeNumber(message.recipient_count)}{' '}
                                            {pluralize(message.recipient_count, 'contact')}
                                            {message.recipient_excluded_count > 0 && (
                                                <span className="text-gray-400">
                                                    (
                                                    {maskWholeNumber(
                                                        message.recipient_excluded_count,
                                                    )}{' '}
                                                    excluded)
                                                </span>
                                            )}
                                        </p>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content inverse>
                                            <p>
                                                Messages will be sent to contacts matching your
                                                filters at the time of sending, less those who have
                                                not opted-in or have unsubscribed.
                                            </p>
                                            <Tooltip.Arrow inverse />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            </Row.Value>
                        </Row.Root>
                        <Separator />
                        <Row.Root>
                            <Row.Title className={'col-span-2 md:col-span-1'}>From:</Row.Title>
                            <Row.Value className={'col-span-10 md:col-span-11'}>
                                {sender || title}
                            </Row.Value>
                        </Row.Root>
                        <Separator />
                        {message.channel === 'email' && (
                            <Row.Root>
                                <Row.Title className={'col-span-2 md:col-span-1'}>
                                    Subject:
                                </Row.Title>
                                <Row.Value className={'col-span-10 md:col-span-11'}>
                                    {message.subject}
                                </Row.Value>
                            </Row.Root>
                        )}
                    </div>

                    {message.channel === MessageChannel.SMS && (
                        <MessageBuilder.Root
                            messageId={message.id}
                            variant="sms"
                            className="border-none shadow-none"
                        >
                            <MessageBuilder.SmsPreview />
                        </MessageBuilder.Root>
                    )}
                    {message.channel === MessageChannel.EMAIL && (
                        <EmailPreview messageId={message.id} autoSaving={isPending} />
                    )}
                </Box>
                <StepNavigation isNextDisabled={isPending} />
            </div>
        </Form.GivebutterFormProvider>
    );
};

export { StepThree };
